import { signal, computed, batch } from '@preact/signals'
import type { Client } from '../../../client/protocol.ts'
import type { SessionsParams } from '../../../protocol/views.ts'
import type { PageOwner } from '../shell/owner.ts'
import { watch } from '../store/watch.ts'
import { duration } from '../ui/tokens.ts'
import { groupHeadings, type Group } from '../../../client/status.ts'
import { createDisplay, type SourceGroup, type DisplaySource } from './display.ts'
import type { Anchor } from './geometry.ts'
import { sessionsCapabilities, wireQuery, type PageQuery, type SessionsCapabilities } from './contracts.ts'

export const backends = [['all', 'All'], ['claude', 'Claude Code'], ['codex', 'Codex'], ['agy', 'agy']] as const
export type BackendPage = typeof backends[number][0]
export interface SessionsIntent { words: string; backend: BackendPage; segment: 'workspace' | 'history'; directory: string; includeChildren: boolean; partition?: Group }
export function createSessionsPage(client: Client, owner: PageOwner, capabilities: SessionsCapabilities = sessionsCapabilities, initialView?: 'running') {
  const selected = signal<BackendPage>('all'), connection = signal(client.state), title = signal('Device')
  const pages = new Map<BackendPage, ReturnType<typeof createBackend>>()
  function createBackend(backend: BackendPage) {
    type Selection = SessionsIntent
    type Slot = { cursor: SessionsParams['cursor']; next?: SessionsParams['cursor']; stop(): void; loaded: boolean; admitted: boolean }
    type Chain = { group: SourceGroup; depth: number; slots: Slot[] }
    type Window = { selection: Selection; model: ReturnType<typeof createDisplay>; anchor?: Anchor; chains: Map<SourceGroup, Chain>; ready: boolean; deviceName?: string }
    const intent = signal<SessionsIntent>({ words: '', backend, segment: capabilities.workspace ? 'workspace' : 'history', directory: '', includeChildren: false, ...(initialView === 'running' && backend === 'all' ? { partition: 'running-unattended' as const } : {}) })
    const segment = computed(() => intent.value.segment), query = computed(() => intent.value.words)
    const directory = computed(() => intent.value.directory), includeChildren = computed(() => intent.value.includeChildren)
    const loading = signal(true), error = signal(''), limit = signal(capabilities.workspace ? groupHeadings.length * 40 : 40), paging = signal(false), nextCursor = signal<SessionsParams['cursor']>(undefined)
    const groupPaging = signal<readonly { group: SourceGroup; total?: number; next?: SessionsParams['cursor']; loading: boolean; limit: number }[]>([])
    const selection = (): Selection => ({ ...intent.peek(), words: intent.peek().words.trim() })
    const workspace = (value: Selection) => value.segment === 'workspace' && !value.words
    const make = (value: Selection, previous?: Window): Window => {
      const groups: SourceGroup[] = workspace(value) ? value.partition ? [value.partition] : groupHeadings.map(({ group }) => group) : ['history']
      return { selection: { ...value }, anchor: previous?.anchor, ready: false,
        chains: new Map(groups.map(group => [group, { group, depth: previous?.chains.get(group)?.depth ?? 1, slots: [] }])),
        model: createDisplay({ mode: workspace(value) ? 'workspace' : 'history', tapGuard: duration['tap-guard'], lastInput: capabilities.lastInput }) }
    }
    const windows: { active?: Window; pending?: Window; saved?: Window } = { pending: make(selection()) }
    // Pending reception is private, even during the first load and after an initial failure.
    const placeholder = createDisplay({ mode: workspace(selection()) ? 'workspace' : 'history' }), display = signal(placeholder)
    let stopDelay = () => {}
    const chosen = () => windows.pending ?? windows.active!
    const current = (window: Window) => windows.pending === window || !windows.pending && windows.active === window
    const same = (a: Selection, b: Selection) => a.words === b.words && a.backend === b.backend && a.segment === b.segment && a.directory === b.directory && a.includeChildren === b.includeChildren && a.partition === b.partition
    const intended = (window: Window) => same(window.selection, selection())
    const params = (window: Window, group?: SourceGroup): PageQuery => {
      const value = window.selection
      return { backend: value.backend === 'all' ? undefined : value.backend, limit: 40, query: value.words || undefined,
        archived: !workspace(value), includeChildren: value.segment === 'history' ? value.includeChildren : false,
        ...(capabilities.under && value.segment === 'history' && value.directory ? { under: value.directory } : {}),
        ...(workspace(value) && (group && group !== 'history' || value.partition) ? { filter: group && group !== 'history' ? group : value.partition } : {}) }
    }
    const stop = (window: Window | undefined) => { window?.chains.forEach(chain => chain.slots.splice(0).forEach(slot => slot.stop())) }
    const dispose = (window: Window | undefined) => { if (window) { stop(window); window.model.dispose() } }
    const depth = (window: Window) => [...window.chains.values()].reduce((sum, chain) => sum + chain.depth * 40, 0)
    const restore = (window: Window) => { limit.value = depth(window); display.value = window.model }
    const complete = (window: Window) => [...window.chains.values()].every(chain => chain.slots.length >= chain.depth && chain.slots.every(slot => slot.loaded))
    function publish(window: Window) {
      if (!current(window)) return
      loading.value = windows.pending === window || !complete(window)
      paging.value = [...window.chains.values()].some(chain => chain.slots.some(slot => !slot.loaded))
      const groups = [...window.chains.values()].map(chain => ({ group: chain.group, total: window.model.state.partitions.get(`${chain.group}:page:0`)?.total,
        next: chain.slots.at(-1)?.next, loading: !chain.slots.length || chain.slots.some(slot => !slot.loaded), limit: chain.depth * 40 }))
      if (JSON.stringify(groups) !== JSON.stringify(groupPaging.peek())) groupPaging.value = groups
      nextCursor.value = groups.find(group => group.next)?.next
      limit.value = depth(window)
    }
    function promote(window: Window) {
      const previous = windows.active
      if (previous && same(previous.selection, window.selection)) {
        window.anchor = previous.anchor
        const staged = window.model
        const sources: DisplaySource[] = [...staged.state.sources].map(([source, refs]) => {
          const part = staged.state.partitions.get(source)!
          return { source, group: part.group, root: part.root, data: { total: part.total, next: part.next, rows: [...refs].map(ref => staged.rows.item(ref).peek()!.row) } }
        })
        previous.model.dispatch({ type: 'replace', sources, now: Date.now() })
        window.model = previous.model
        staged.dispose()
      }
      windows.pending = undefined; windows.active = window; window.ready = true
      if (!window.selection.words) {
        if (windows.saved !== previous && windows.saved !== window) dispose(windows.saved)
        windows.saved = undefined
      }
      display.value = window.model
      if (previous !== window && previous !== windows.saved && previous?.model !== window.model) dispose(previous)
    }
    function failed(window: Window) {
      const relevant = intended(window)
      stop(window)
      if (windows.pending === window && windows.active?.ready) {
        windows.pending = undefined
        if (window !== windows.saved) dispose(window)
        restore(windows.active)
      }
      loading.value = paging.value = false
      if (relevant) error.value = 'Couldn’t load sessions.'
    }
    function follow(window: Window, chain: Chain, index: number, cursor: SessionsParams['cursor']) {
      if (index >= chain.depth) return
      const old = chain.slots[index]
      if (old && old.cursor?.ref === cursor?.ref && old.cursor?.orderAt === cursor?.orderAt) return
      chain.slots.splice(index).forEach(slot => slot.stop())
      if (cursor) startSlot(window, chain, index, cursor)
      else {
        const prefix = `${chain.group}:page:`
        for (const source of window.model.state.sources.keys()) if (source.startsWith(prefix) && Number(source.slice(prefix.length)) >= index) window.model.dispatch({ type: 'drop', source })
        chain.depth = Math.min(chain.depth, index)
      }
    }
    function startSlot(window: Window, chain: Chain, index: number, cursor: SessionsParams['cursor']) {
      const slot: Slot = { cursor, stop() {}, loaded: false, admitted: false }, source = `${chain.group}:page:${index}`
      chain.slots[index] = slot
      const owned = () => current(window) && window.chains.get(chain.group) === chain && chain.slots[index] === slot
      const active = () => owned() && intended(window)
      const queryOwner = { get disposed() { return owner.disposed }, get active() { return owner.active && selected.peek() === backend }, own: owner.own.bind(owner), onActive: owner.onActive.bind(owner),
        watchStatus(token: object, status: 'pending' | 'current' | 'error' | undefined) {
          // A watch going pending (reconnect replay) starts a new reception cycle: this page counts toward a
          // complete window again only after its next snapshot, never on a flag kept from the previous cycle.
          if (status === 'pending' && owned()) slot.loaded = false
          owner.watchStatus(token, !status || active() ? status : undefined)
        } }
      slot.stop = watch(queryOwner, client, 'sessions', wireQuery({ ...params(window, chain.group), cursor }), {
        onSnapshot(data) {
          if (!active()) return
          batch(() => {
            if (!slot.admitted) {
              const prefix = `${chain.group}:page:`
              if (!index) for (const old of window.model.state.sources.keys()) if (old.startsWith(prefix) && old !== source) window.model.dispatch({ type: 'drop', source: old })
            }
            window.model.dispatch({ type: 'snapshot', source, group: chain.group, root: index === 0, data, now: Date.now() })
            slot.loaded = slot.admitted = true; slot.next = data.next
            if (!index) window.deviceName = data.deviceName
            follow(window, chain, index + 1, data.next)
            if (windows.pending === window && complete(window)) { title.value = window.deviceName || 'Device'; promote(window) }
            publish(window)
          })
        },
        onPatch(ops) {
          if (!active() || !slot.loaded) return
          batch(() => {
            window.model.dispatch({ type: 'patch', source, ops, now: Date.now() })
            for (const op of ops) if (op.op === 'cursor') { slot.next = op.next; follow(window, chain, index + 1, op.next) }
            if (windows.pending === window && complete(window)) { title.value = window.deviceName || 'Device'; promote(window) }
            publish(window)
          })
        },
        onReset() { if (active()) batch(() => begin(window.selection, window)) },
        onError() { if (owned()) batch(() => failed(window)) },
      })
      publish(window)
    }
    function begin(value: Selection, previous?: Window) {
      if (selected.peek() !== backend) return
      const committed = windows.active?.ready ? windows.active : undefined
      if (value.words && committed && !committed.selection.words && !windows.saved) windows.saved = committed
      stop(windows.pending)
      if (windows.pending !== windows.saved) dispose(windows.pending)
      stop(windows.active)
      const next = make(value, previous)
      windows.pending = next; error.value = ''
      for (const chain of next.chains.values()) startSlot(next, chain, 0, undefined)
    }
    function submit() {
      const value = selection(), window = chosen()
      begin(value, same(value, window.selection) ? window : undefined)
    }
    function search(text: string) {
      const clearing = !!intent.peek().words.trim() && !text.trim()
      intent.value = { ...intent.peek(), words: text }; error.value = ''; stopDelay()
      if (clearing) restoreQueryFree()
      if (!intended(chosen())) { stop(windows.pending); stop(windows.active); loading.value = paging.value = false }
      stopDelay = owner.timeout(() => {
        if (selected.peek() !== backend) return
        const window = chosen()
        if (!same(selection(), window.selection) || ![...window.chains.values()].some(chain => chain.slots.length)) submit()
      }, 150)
    }
    function restoreQueryFree() {
      const target = windows.saved?.ready ? windows.saved : windows.active?.ready && !windows.active.selection.words ? windows.active : undefined
      if (!target) return
      if (windows.pending !== target) dispose(windows.pending)
      if (windows.active !== target && windows.active !== windows.saved) dispose(windows.active)
      windows.pending = windows.saved = undefined; windows.active = target; error.value = ''
      restore(target)
    }
    function cancelSearch() { intent.value = { ...intent.peek(), words: '' }; stopDelay(); restoreQueryFree(); submit() }
    function filter(value: Partial<Omit<SessionsIntent, 'backend' | 'words'>>) {
      stopDelay(); intent.value = { ...intent.peek(), ...value }; submit()
    }
    function retry() { stopDelay(); submit() }
    function hide() { stopDelay(); stop(windows.pending); stop(windows.active) }
    function open() { if (selected.peek() === backend && owner.active) submit() }
    const canMore = () => !loading.peek() && !paging.peek() && !error.peek() && groupPaging.peek().some(group => group.next && (capabilities.continuation || group.limit < 400))
    const activity = owner.onActive(active => { if (!active) hide(); else if (selected.peek() === backend) open() })
    return { intent, segment, query, directory, includeChildren, loading, error, limit, display, paging, groupPaging, next: nextCursor,
      total: computed(() => { const roots = [...display.value.partitions.value.values()].filter(part => part.root); return roots.length ? roots.reduce((sum, part) => sum + part.total, 0) : undefined }),
      get isWorkspace() { return display.peek().state.mode === 'workspace' },
      get scopeQuery() { const { limit: _limit, cursor: _cursor, ...scope } = params(windows.active ?? chosen()); return scope },
      get canMore() { return canMore() },
      get anchor() { return (windows.active ?? chosen()).anchor }, set anchor(value: Anchor | undefined) { (windows.active ?? chosen()).anchor = value },
      open, hide, retry, search, cancelSearch,
      setSegment(value: 'workspace' | 'history') { if ((value !== segment.peek() || intent.peek().partition !== undefined) && (value !== 'workspace' || capabilities.workspace)) filter({ segment: value, partition: undefined }) },
      setDirectory(value: string) { if (capabilities.under) filter({ directory: value }) },
      setChildren(value: boolean) { filter({ includeChildren: value }) },
      showRunning() { stopDelay(); intent.value = { ...intent.peek(), words: '', segment: 'workspace', partition: 'running-unattended' }; submit() },
      more(group?: SourceGroup) {
        const window = windows.active
        if (!window || windows.pending || !canMore()) return
        const chain = [...window.chains.values()].find(chain => (!group || group === chain.group) && chain.slots.at(-1)?.next && (capabilities.continuation || chain.depth * 40 < 400))
        const tail = chain?.slots.at(-1)
        if (!chain || !tail?.next) return
        chain.depth++; startSlot(window, chain, chain.slots.length, tail.next)
      },
      dispose() { activity(); stopDelay(); for (const window of new Set([windows.active, windows.pending, windows.saved])) dispose(window); placeholder.dispose() },
    }
  }
  function current() { const key = selected.peek(); let page = pages.get(key); if (!page) { page = createBackend(key); pages.set(key, page) } return page }
  function select(value: BackendPage, view?: 'running') {
    if (value !== selected.peek()) { current().hide(); selected.value = value }
    else if (!view) return
    const target = current()
    if (view === 'running') target.showRunning(); else target.open()
  }
  const unsubscribe = client.subscribe(state => { connection.value = state })
  owner.own(() => { unsubscribe(); pages.forEach(page => page.dispose()) })
  // Devices without a published name keep the honest page title.
  current().open()
  return { selected, connection, title, pages, current, select, capabilities }
}
export type SessionsPage = ReturnType<typeof createSessionsPage>
